import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AdminTableShimmer } from '../common/Shimmer.jsx';
import ListPagination from '../common/ListPagination.jsx';
import { PAGE_SIZE_OPTIONS } from '../../utils/listPagination.js';
import { buildApiUrl } from '../../utils/apiBase.js';
import { devError } from '../../utils/logger.js';
import AdminRepository from '../../services/AdminRepository.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminLogViewer.css';
import './AdminClinicList.css';

const ACTION_LABELS = {
  'signup': '新規登録',
  'change-password': 'パスワード変更',
  'confirm-reset-password': 'パスワードリセット',
  'update-clinic': 'クリニック情報更新',
  'create-user': 'ユーザー作成',
  'update-user': 'ユーザー更新',
  'delete-user': 'ユーザー削除',
  'add-license': 'ライセンス追加',
  'cancel-license': 'ライセンス解約',
  'update-profile': 'プロフィール更新',
  'admin-create-demo': 'デモ作成',
  'admin-delete-demo': 'デモ削除',
  'admin-delete-clinic': 'クリニック削除',
  'admin-restore-clinic': 'クリニック復元',
  'admin-purge-deleted-clinic': 'クリニック完全削除（期限後）',
  'admin-set-license': 'ライセンス設定',
  'admin-create-product': '製品作成',
  'admin-update-product': '製品更新',
  'admin-delete-product': '製品削除',
  'admin-create-stripe-product': 'Stripe製品作成',
  'admin-update-stripe-product': 'Stripe製品更新',
  'admin-delete-user': 'ユーザー削除(Admin)',
  'admin-create-user': 'ユーザー作成(Admin)',
  'admin-update-roles': 'ロール更新',
  'admin-update-settings': '設定更新',
  'health-check-run': 'ヘルスチェック（定期・旧）',
  'health-check-run-l2': 'ヘルスチェック L2',
  'health-check-run-l3': 'ヘルスチェック L3',
  'admin-create-announcement': 'お知らせ作成',
  'admin-update-announcement': 'お知らせ更新',
  'admin-delete-announcement': 'お知らせ削除',
  'admin-send-individual': '個別通知送信',
  'contact-update-status': '問い合わせステータス変更',
  'contact-delete': '問い合わせ削除',
  'stripe-invoice.paid': 'Stripe 支払い完了',
  'stripe-invoice.payment_failed': 'Stripe 支払い失敗',
  'stripe-customer.subscription.deleted': 'Stripe サブスク解約',
  'stripe-payment_method.attached': 'Stripe 支払い方法追加',
  'stripe-payment_method.detached': 'Stripe 支払い方法削除',
  'admin-update-product-order': '製品表示順更新',
  'email-template-reset': 'メールテンプレート復帰',
  'login': 'ログイン',
  'logout': 'ログアウト',
  'myo-ai-generate': 'マイオ: AI テキスト生成',
  'myo-ai-diagnosis': 'マイオ: AI 画像診断',
  'myo-ocr': 'マイオ: メモ OCR',
  'myo-house-sync-diary': 'マイオ: Myo House 日記同期',
  'myo-house-link-verify': 'マイオ: Myo House 連携確認',
  'myo-house-link-create': 'マイオ: Myo House 連携 QR 発行',
  'myo-temp-login-exchange': 'マイオ: 臨時ログイン',
  'myo-admin-clinic-create': 'マイオ: クリニック作成',
  'myo-admin-transfer-patient': 'マイオ: 患者転送',
  'myo-admin-issue-temp-login': 'マイオ: 臨時ログイン発行',
  'myo-admin-revoke-temp-login': 'マイオ: 臨時ログイン失効',
  'myo-cron-cleanup-tokens': 'マイオ: 期限切れトークン清理',
  'myo-patient-create': 'マイオ: 患者作成',
  'myo-patient-update': 'マイオ: 患者更新',
  'myo-patient-delete': 'マイオ: 患者削除',
  'myo-image-upload': 'マイオ: 画像アップロード',
  'myo-image-update': 'マイオ: 画像更新',
  'myo-image-delete': 'マイオ: 画像削除',
  'myo-image-holder-create': 'マイオ: 画像ホルダー作成',
  'myo-image-holder-delete': 'マイオ: 画像ホルダー削除',
  'myo-diagnosis-save': 'マイオ: 分析データ保存',
  'myo-warning-approve': 'マイオ: 分析警告承認',
  'myo-me-check-save': 'マイオ: MEチェック保存',
  'myo-me-check-delete': 'マイオ: MEチェック削除',
  'myo-video-create': 'マイオ: 動画アップロード',
  'myo-video-delete': 'マイオ: 動画削除',
  'myo-memo-ocr-save': 'マイオ: メモ OCR 保存',
  'myo-report-generate': 'マイオ: レポート生成',
  'myo-report-update': 'マイオ: レポート更新',
  'myo-report-delete': 'マイオ: レポート削除',
  'myo-diagnosis-delete': 'マイオ: 分析データ削除',
  'myo-patient-restore': 'マイオ: 患者復元',
  'myo-patient-enable-myo': 'マイオ: Myo 利用有効化',
  'myo-questionnaire-save': 'マイオ: 検査データ保存',
  'myo-questionnaire-delete': 'マイオ: 検査データ削除',
  'myo-sleep-questionnaire-save': 'マイオ: 睡眠問診保存',
  'myo-sleep-questionnaire-delete': 'マイオ: 睡眠問診削除',
  'myo-stl-save': 'マイオ: STL データ作成',
  'myo-stl-update': 'マイオ: STL データ更新',
  'myo-stl-delete': 'マイオ: STL データ削除',
  'myo-dental-chart-save': 'マイオ: 歯式データ保存',
  'myo-appliance-save': 'マイオ: 装着装置追加',
  'myo-appliance-delete': 'マイオ: 装着装置削除',
  'myo-insurance-billing-save': 'マイオ: 保険算定保存',
  'myo-insurance-billing-delete': 'マイオ: 保険算定削除',
  'auth.logout': 'インプラント: ログアウト',
  'auth.sso-check': 'インプラント: SSO 確認',
  'auth.verify': 'インプラント: 認証検証',
  'auth.clinic-by-name': 'インプラント: クリニック名検索',
  'auth.clinic-login': 'インプラント: クリニックログイン',
  'auth.clinic.create': 'インプラント: クリニック作成',
  'auth.clinic.update': 'インプラント: クリニック更新',
  'auth.clinic.settings': 'インプラント: クリニック設定更新',
  'auth.user.create': 'インプラント: ユーザー作成',
  'auth.user.update': 'インプラント: ユーザー更新',
  'auth.staff.create': 'インプラント: スタッフ作成',
  'auth.staff.update': 'インプラント: スタッフ更新',
  'auth.staff.delete': 'インプラント: スタッフ削除',
  'clinic.implant-director.update': 'インプラント: 責任者更新',
  'clinic.email-verification.resend': 'インプラント: メール認証再送',
  'temp-login.exchange': 'インプラント: 臨時ログイン交換',
  'admin.clinic.create': 'インプラント: 管理者クリニック作成',
  'admin.user.create': 'インプラント: 管理者ユーザー作成',
  'admin.clinic-user.create': 'インプラント: 管理者クリニックユーザー作成',
  'admin.delete-user': 'インプラント: 管理者ユーザー削除',
  'admin.issue-temp-login': 'インプラント: 臨時ログイン発行',
  'admin.revoke-temp-login': 'インプラント: 臨時ログイン失効',
  'admin.cleanup-expired-tokens': 'インプラント: 期限切れトークン清理',
  'implant-ai-analyze-pdf': 'インプラント: PDF AI 解析',
  'implant-exam-extract-report': 'インプラント: 検査レポート抽出',
  'implant-exam-extract-pt': 'インプラント: 検査 PT 抽出',
  'implant-ocr': 'インプラント: OCR',
  'implant-image-composite': 'インプラント: 画像合成',
  'plum-launch.issue': 'Plum: issue',
  'plum-launch.resolve': 'Plum: resolve',
  'plum-launch.session': 'Plum: session',
  'plum-launch.consume': 'Plum: consume',
  'plum-launch.ack': 'Plum: ack',
  'plum-launch.register': 'Plum: register',
  'plum-launch.login': 'Plum: login',
};

const SYSTEM_PROCESS_ACTION_PATTERNS = [
  /^health-check-/,
  /^stripe[-.]/,
  /^admin-(create|update|delete)-product/,
  /^admin-update-product-order$/,
  /^admin-(create|update)-stripe/,
  /^email-template-/,
  /^admin-export-/,
  /^myo-cron-/,
  /^admin\.cleanup-expired-tokens$/,
];

function isSystemProcessAuditLog(log) {
  const action = log?.action || '';
  if (log?.uid === 'system' || log?.uid === 'batch') return true;
  return SYSTEM_PROCESS_ACTION_PATTERNS.some((re) => re.test(action));
}

function isSynapseMyoStream(log) {
  return log?.stream === 'synapsemyo' || log?.producer === 'synapsemyo';
}

function isSynapseImplantStream(log) {
  return log?.stream === 'synapseimplant' || log?.producer === 'synapseimplant';
}

function resolveClinicDisplay(log, clinicMap) {
  if (log?.clinicId) {
    return clinicMap[log.clinicId] || log.clinicId;
  }
  if (isSynapseMyoStream(log)) {
    const action = log?.action || '';
    if (action.startsWith('myo-admin-') || action.startsWith('admin-')) {
      return 'マイオ管理者（クリニック外）';
    }
    if (log?.uid === 'external-myo-house') {
      return 'Myo House 外部連携';
    }
  }
  if (isSynapseImplantStream(log)) {
    const action = log?.action || '';
    if (action.startsWith('admin.') || action.startsWith('auth.clinic.create')) {
      return 'インプラント管理者（クリニック外）';
    }
    if (String(log?.uid || '').startsWith('plum:')) {
      return 'PlumConnect（未認証）';
    }
  }
  if (log?.stream === 'adminHub' || log?.producer === 'adminHub') {
    return 'AdminHub 管理者（クリニック外）';
  }
  if (log?.uid === 'system' || log?.uid === 'batch') {
    return 'システム（クリニック外）';
  }
  if (isSystemProcessAuditLog(log)) {
    return 'システム処理（クリニック外）';
  }
  if ((log?.action || '').startsWith('admin-')) {
    return 'AdminHub 管理者（クリニック外）';
  }
  return '—';
}

function formatAuditActionLabel(log) {
  const label = ACTION_LABELS[log?.action] || log?.action || '—';
  return { label, isSystem: isSystemProcessAuditLog(log) };
}

/** 監査アクション種別の表示グループ（折りたたみ・一括選択用） */
const AUDIT_ACTION_GROUPS = [
  {
    id: 'auth',
    label: '認証・アカウント',
    actions: ['signup', 'change-password', 'confirm-reset-password', 'update-profile'],
  },
  {
    id: 'clinic-user',
    label: 'クリニック・ユーザー',
    actions: ['update-clinic', 'create-user', 'update-user', 'delete-user'],
  },
  {
    id: 'license',
    label: 'ライセンス・契約',
    actions: ['add-license', 'cancel-license', 'admin-set-license'],
  },
  {
    id: 'admin-org',
    label: '管理：組織・ユーザー',
    actions: [
      'admin-create-demo', 'admin-delete-demo', 'admin-delete-clinic',
      'admin-create-user', 'admin-delete-user', 'admin-update-roles', 'admin-update-settings',
    ],
  },
  {
    id: 'admin-product',
    label: '管理：製品・Stripe',
    actions: [
      'admin-create-product', 'admin-update-product', 'admin-delete-product',
      'admin-create-stripe-product', 'admin-update-stripe-product',
    ],
  },
  {
    id: 'announcement',
    label: 'お知らせ・通知',
    actions: [
      'admin-create-announcement', 'admin-update-announcement',
      'admin-delete-announcement', 'admin-send-individual',
    ],
  },
  {
    id: 'contact',
    label: '問い合わせ',
    actions: ['contact-update-status', 'contact-delete'],
  },
  {
    id: 'stripe',
    label: 'Stripe Webhook',
    actions: [
      'stripe-invoice.paid', 'stripe-invoice.payment_failed',
      'stripe-customer.subscription.deleted',
      'stripe-payment_method.attached', 'stripe-payment_method.detached',
    ],
  },
  {
    id: 'health',
    label: 'ヘルスチェック',
    actions: ['health-check-run', 'health-check-run-l2', 'health-check-run-l3'],
  },
  {
    id: 'synapsemyo',
    label: 'マイオ',
    actions: [
      'login', 'logout', 'update-clinic', 'update-user',
      'myo-ai-generate', 'myo-ai-diagnosis', 'myo-ocr',
      'myo-house-sync-diary', 'myo-house-link-verify', 'myo-house-link-create',
      'myo-temp-login-exchange',
      'myo-admin-clinic-create', 'myo-admin-transfer-patient',
      'admin-create-user', 'admin-delete-user',
      'myo-admin-issue-temp-login', 'myo-admin-revoke-temp-login', 'myo-cron-cleanup-tokens',
      'myo-patient-create', 'myo-patient-update', 'myo-patient-delete',
      'myo-patient-restore', 'myo-patient-enable-myo',
      'myo-image-upload', 'myo-image-update', 'myo-image-delete',
      'myo-image-holder-create', 'myo-image-holder-delete',
      'myo-diagnosis-save', 'myo-diagnosis-delete', 'myo-warning-approve',
      'myo-me-check-save', 'myo-me-check-delete',
      'myo-video-create', 'myo-video-delete',
      'myo-memo-ocr-save',
      'myo-report-generate', 'myo-report-update', 'myo-report-delete',
      'myo-questionnaire-save', 'myo-questionnaire-delete',
      'myo-sleep-questionnaire-save', 'myo-sleep-questionnaire-delete',
      'myo-stl-save', 'myo-stl-update', 'myo-stl-delete',
      'myo-dental-chart-save',
      'myo-appliance-save', 'myo-appliance-delete',
      'myo-insurance-billing-save', 'myo-insurance-billing-delete',
    ],
  },
  {
    id: 'synapseimplant',
    label: 'インプラント',
    actions: [
      'auth.logout', 'auth.sso-check', 'auth.verify', 'auth.clinic-by-name', 'auth.clinic-login',
      'auth.clinic.create', 'auth.clinic.update', 'auth.clinic.settings',
      'auth.user.create', 'auth.user.update',
      'auth.staff.create', 'auth.staff.update', 'auth.staff.delete',
      'clinic.implant-director.update', 'clinic.email-verification.resend',
      'temp-login.exchange',
      'admin.clinic.create', 'admin.user.create', 'admin.clinic-user.create',
      'admin.delete-user', 'admin.issue-temp-login', 'admin.revoke-temp-login',
      'admin.cleanup-expired-tokens',
      'implant-ai-analyze-pdf', 'implant-exam-extract-report', 'implant-exam-extract-pt',
      'implant-ocr', 'implant-image-composite',
      'plum-launch.issue', 'plum-launch.resolve', 'plum-launch.session',
      'plum-launch.consume', 'plum-launch.ack', 'plum-launch.register', 'plum-launch.login',
    ],
  },
];

const ALL_AUDIT_ACTIONS = Object.keys(ACTION_LABELS);

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

const LOG_STREAM_OPTIONS = [
  { id: 'adminHub', label: 'AdminHub' },
  { id: 'userHub', label: 'UserHub' },
  { id: 'authHub', label: 'AuthHub' },
  { id: 'synapsemyo', label: 'マイオ' },
  { id: 'synapseimplant', label: 'インプラント' },
  { id: 'synapseApiGateway', label: 'API Gateway' },
];

/** 初回表示を速くするため AdminHub のみ（他はチップで追加） */
const DEFAULT_LOG_STREAMS = ['adminHub'];

const SYSTEM_METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const SYSTEM_STATUS_OPTIONS = ['200', '400', '401', '403', '404', '500'];

const STREAM_LABELS = Object.fromEntries(LOG_STREAM_OPTIONS.map((s) => [s.id, s.label]));

function getDefaultLogStartDate() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

function createAllCollapsedAuditGroupSet() {
  return new Set(AUDIT_ACTION_GROUPS.map((g) => g.id));
}

function createFullSet(items) {
  return new Set(items);
}

function isAuditSetAllEnabled(set) {
  return set.size >= ALL_AUDIT_ACTIONS.length;
}

function isAuditActionEnabled(set, actionKey) {
  return isAuditSetAllEnabled(set) || set.has(actionKey);
}

function normalizeAuditActionSet(set) {
  if (set.size >= ALL_AUDIT_ACTIONS.length) return createFullSet(ALL_AUDIT_ACTIONS);
  return set;
}

function setAuditGroupEnabled(setter, groupActions, enabled) {
  setter((prev) => {
    const base = isAuditSetAllEnabled(prev) ? new Set(ALL_AUDIT_ACTIONS) : new Set(prev);
    groupActions.forEach((key) => {
      if (enabled) base.add(key);
      else base.delete(key);
    });
    if (base.size === 0) return new Set();
    return normalizeAuditActionSet(base);
  });
}

function toggleAuditAction(setter, actionKey) {
  setter((prev) => {
    const base = isAuditSetAllEnabled(prev) ? new Set(ALL_AUDIT_ACTIONS) : new Set(prev);
    if (base.has(actionKey)) base.delete(actionKey);
    else base.add(actionKey);
    if (base.size === 0) return new Set();
    if (base.size >= ALL_AUDIT_ACTIONS.length) return createFullSet(ALL_AUDIT_ACTIONS);
    return base;
  });
}

function AuditActionGroup({
  group,
  auditActions,
  setAuditActions,
  collapsed,
  onToggleCollapse,
}) {
  const groupCheckboxRef = useRef(null);
  const enabledCount = group.actions.filter((key) => isAuditActionEnabled(auditActions, key)).length;
  const allEnabled = enabledCount === group.actions.length;
  const someEnabled = enabledCount > 0 && !allEnabled;

  useEffect(() => {
    if (groupCheckboxRef.current) {
      groupCheckboxRef.current.indeterminate = someEnabled;
    }
  }, [someEnabled, allEnabled]);

  return (
    <div className={`admin-log-action-group${collapsed ? ' admin-log-action-group--collapsed' : ''}`}>
      <div className="admin-log-action-group__header">
        <label className="admin-log-action-group__label">
          <input
            ref={groupCheckboxRef}
            type="checkbox"
            checked={allEnabled}
            onChange={(e) => setAuditGroupEnabled(setAuditActions, group.actions, e.target.checked)}
          />
          <span className="admin-log-action-group__name">{group.label}</span>
          <span className="admin-log-action-group__count">{enabledCount}/{group.actions.length}</span>
        </label>
        <button
          type="button"
          className="admin-log-action-group__toggle"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `${group.label}を展開` : `${group.label}を格納`}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={collapsed ? '9 18 15 12 9 6' : '6 9 12 15 18 9'} />
          </svg>
        </button>
      </div>
      {!collapsed && (
        <div className="admin-log-action-group__items">
          {group.actions.map((actionKey) => (
            <label key={actionKey} className="admin-log-checkbox-item admin-log-checkbox-item--stacked">
              <input
                type="checkbox"
                checked={isAuditActionEnabled(auditActions, actionKey)}
                onChange={() => toggleAuditAction(setAuditActions, actionKey)}
              />
              <span>{ACTION_LABELS[actionKey] || actionKey}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function toggleSetValue(setter, allItems, value) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    if (next.size === 0) return createFullSet(allItems);
    if (next.size === allItems.length) return createFullSet(allItems);
    return next;
  });
}

function appendAuditActionsParam(params, auditActions) {
  if (isAuditSetAllEnabled(auditActions)) return;
  const enabled = ALL_AUDIT_ACTIONS.filter((key) => isAuditActionEnabled(auditActions, key));
  if (enabled.length === 0) {
    params.set('actions', '__none__');
    return;
  }
  params.set('actions', enabled.join(','));
}

function appendEnabledCsv(params, key, allItems, enabledSet, { always = false } = {}) {
  if (enabledSet.size === 0) {
    params.set(key, '__none__');
    return;
  }
  if (!always && enabledSet.size >= allItems.length) return;
  params.set(key, [...enabledSet].join(','));
}

function statusClass(code) {
  if (code >= 200 && code < 300) return 'log-status-2xx';
  if (code >= 400 && code < 500) return 'log-status-4xx';
  if (code >= 500) return 'log-status-5xx';
  return '';
}

const AdminLogViewer = ({ clinics }) => {
  const probe = useScreenProbe('logs');
  const adminRepo = AdminRepository.getInstance();
  const [tab, setTab] = useState('audit');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1] || 50);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCache, setPageCache] = useState({});
  const [selectedLog, setSelectedLog] = useState(null);
  const [fetched, setFetched] = useState(false);
  const [clinicDirectory, setClinicDirectory] = useState(() => adminRepo.clinicsCache || []);
  const [clinicUsers, setClinicUsers] = useState([]);
  const [clinicUsersLoading, setClinicUsersLoading] = useState(false);
  const [auditActions, setAuditActions] = useState(() => createFullSet(ALL_AUDIT_ACTIONS));
  const [collapsedAuditGroups, setCollapsedAuditGroups] = useState(createAllCollapsedAuditGroupSet);
  const [logStreams, setLogStreams] = useState(() => createFullSet(DEFAULT_LOG_STREAMS));
  const [systemMethods, setSystemMethods] = useState(() => createFullSet(SYSTEM_METHOD_OPTIONS));
  const [systemStatusCodes, setSystemStatusCodes] = useState(() => createFullSet(SYSTEM_STATUS_OPTIONS));

  const pageCacheRef = useRef(pageCache);
  const goToPageReqIdRef = useRef(0);
  useEffect(() => { pageCacheRef.current = pageCache; }, [pageCache]);

  useEffect(() => {
    if (clinics?.length) return;
    let cancelled = false;
    adminRepo.getAllClinics()
      .then((list) => {
        if (!cancelled) setClinicDirectory(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setClinicDirectory([]);
      });
    return () => { cancelled = true; };
  }, [adminRepo, clinics?.length]);

  const [filters, setFilters] = useState({
    clinicId: '',
    clinicNameSearch: '',
    uid: '',
    startDate: getDefaultLogStartDate(),
    endDate: '',
    deployEnv: 'prod',
  });

  useEffect(() => {
    if (!filters.clinicId) {
      setClinicUsers([]);
      return undefined;
    }
    let cancelled = false;
    setClinicUsersLoading(true);
    adminRepo.getClinicUsers(filters.clinicId)
      .then((data) => {
        if (!cancelled) setClinicUsers(data.users || []);
      })
      .catch(() => {
        if (!cancelled) setClinicUsers([]);
      })
      .finally(() => {
        if (!cancelled) setClinicUsersLoading(false);
      });
    return () => { cancelled = true; };
  }, [adminRepo, filters.clinicId]);

  const buildLogQueryParams = useCallback((activeFilters, startAfter, limit = pageSize, facetOverrides = null) => {
    const params = new URLSearchParams();
    if (activeFilters.clinicId) {
      params.set('clinicId', activeFilters.clinicId);
    } else if (activeFilters.clinicNameSearch?.trim()) {
      params.set('clinicName', activeFilters.clinicNameSearch.trim());
    }
    if (activeFilters.uid) params.set('uid', activeFilters.uid);
    if (activeFilters.startDate) params.set('startDate', activeFilters.startDate);
    if (activeFilters.endDate) {
      params.set('endDate', new Date(activeFilters.endDate + 'T23:59:59').toISOString());
    }
    params.set('deployEnv', activeFilters.deployEnv === 'dev' ? 'dev' : 'prod');
    if (startAfter) params.set('startAfter', startAfter);
    params.set('limit', String(limit));

    const activeStreams = facetOverrides?.logStreams ?? logStreams;
    const activeAuditActions = facetOverrides?.auditActions ?? auditActions;
    const activeMethods = facetOverrides?.systemMethods ?? systemMethods;
    const activeStatusCodes = facetOverrides?.systemStatusCodes ?? systemStatusCodes;

    const streamIds = LOG_STREAM_OPTIONS.map((s) => s.id);
    // 全オンでも明示送信（サーバ既定に Gateway が混ざるのを防ぐ）
    appendEnabledCsv(params, 'streams', streamIds, activeStreams, { always: true });

    if (tab === 'audit') {
      appendAuditActionsParam(params, activeAuditActions);
    } else {
      appendEnabledCsv(params, 'methods', SYSTEM_METHOD_OPTIONS, activeMethods);
      appendEnabledCsv(params, 'statusCodes', SYSTEM_STATUS_OPTIONS, activeStatusCodes);
    }
    return params;
  }, [tab, auditActions, logStreams, systemMethods, systemStatusCodes, pageSize]);

  const applyPageEntry = useCallback((page, entry) => {
    setCurrentPage(page);
    setLogs(entry.logs || []);
    setHasMore(Boolean(entry.hasMore));
    setFetched(true);
  }, []);

  const resetPagination = useCallback(() => {
    setPageCache({});
    pageCacheRef.current = {};
    setCurrentPage(1);
    setHasMore(false);
  }, []);

  const fetchLogsPageData = useCallback(async (
    startAfter = null,
    overrideFilters = null,
    facetOverrides = null,
  ) => {
    const activeFilters = overrideFilters || filters;
    const params = buildLogQueryParams(activeFilters, startAfter, pageSize, facetOverrides);
    if (params.get('actions') === '__none__' || params.get('methods') === '__none__'
      || params.get('streams') === '__none__') {
      return { logs: [], lastId: null, hasMore: false, emptyFilter: true };
    }

    const endpoint = tab === 'audit' ? '/api/admin/logs/audit' : '/api/admin/logs/system';
    const res = await fetch(buildApiUrl(`${endpoint}?${params.toString()}`), { credentials: 'include' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'ログの取得に失敗しました');
    }
    const data = await res.json();
    return {
      logs: data.logs || [],
      lastId: data.lastId || null,
      hasMore: Boolean(data.hasMore),
      emptyFilter: false,
    };
  }, [filters, buildLogQueryParams, pageSize, tab]);

  const goToPage = useCallback(async (targetPage, {
    overrideFilters = null,
    forceRefresh = false,
    facetOverrides = null,
  } = {}) => {
    if (!Number.isFinite(targetPage) || targetPage < 1) return;

    const MAX_AHEAD = 50;
    const knownPages = Object.keys(pageCacheRef.current).map(Number);
    const highestKnown = knownPages.length ? Math.max(...knownPages) : 0;
    if (!forceRefresh && targetPage > highestKnown + MAX_AHEAD) {
      setError(`一度に移動できるのは ${MAX_AHEAD} ページ先までです。先に次ページで進んでから指定してください。`);
      return;
    }

    const reqId = ++goToPageReqIdRef.current;
    probe.start('load');
    setLoading(true);
    setError('');

    try {
      let cache = forceRefresh ? {} : { ...pageCacheRef.current };
      if (forceRefresh) {
        pageCacheRef.current = cache;
        setPageCache({});
      }

      if (!forceRefresh && cache[targetPage]) {
        if (reqId !== goToPageReqIdRef.current) return;
        applyPageEntry(targetPage, cache[targetPage]);
        // 最終既知ページの hasMore を維持
        const maxKnown = Math.max(...Object.keys(cache).map(Number));
        setHasMore(Boolean(cache[maxKnown]?.hasMore));
        probe.end('load', 'ok');
        probe.unlock();
        return;
      }

      let startAfter = null;
      for (let page = 1; page <= targetPage; page += 1) {
        if (cache[page] && !forceRefresh) {
          startAfter = cache[page].lastId;
          continue;
        }

        const data = await fetchLogsPageData(
          page === 1 ? null : startAfter,
          overrideFilters,
          facetOverrides,
        );
        if (reqId !== goToPageReqIdRef.current) return;

        if (data.emptyFilter) {
          cache = { 1: { logs: [], lastId: null, hasMore: false } };
          pageCacheRef.current = cache;
          setPageCache(cache);
          applyPageEntry(1, cache[1]);
          setHasMore(false);
          probe.end('load', 'ok');
          probe.unlock();
          return;
        }

        const entry = {
          logs: data.logs,
          lastId: data.lastId,
          hasMore: data.hasMore,
        };
        cache = { ...cache, [page]: entry };
        pageCacheRef.current = cache;
        setPageCache(cache);
        startAfter = data.lastId;

        if (!data.hasMore && page < targetPage) {
          applyPageEntry(page, entry);
          setHasMore(false);
          probe.end('load', 'ok');
          probe.unlock();
          return;
        }
      }

      if (reqId !== goToPageReqIdRef.current) return;
      const finalEntry = cache[targetPage];
      applyPageEntry(targetPage, finalEntry);
      const maxKnown = Math.max(...Object.keys(cache).map(Number));
      setHasMore(Boolean(cache[maxKnown]?.hasMore));
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      if (reqId !== goToPageReqIdRef.current) return;
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      devError('ログ取得エラー:', err);
      setError(err.message);
    } finally {
      if (reqId === goToPageReqIdRef.current) setLoading(false);
    }
  }, [applyPageEntry, fetchLogsPageData, probe]);

  const handleSearch = () => {
    resetPagination();
    goToPage(1, { forceRefresh: true });
  };

  const handleReset = () => {
    const emptyFilters = {
      clinicId: '',
      clinicNameSearch: '',
      uid: '',
      startDate: getDefaultLogStartDate(),
      endDate: '',
      deployEnv: 'prod',
    };
    const resetFacets = {
      logStreams: createFullSet(DEFAULT_LOG_STREAMS),
      auditActions: createFullSet(ALL_AUDIT_ACTIONS),
      systemMethods: createFullSet(SYSTEM_METHOD_OPTIONS),
      systemStatusCodes: createFullSet(SYSTEM_STATUS_OPTIONS),
    };
    setFilters(emptyFilters);
    setAuditActions(resetFacets.auditActions);
    setCollapsedAuditGroups(createAllCollapsedAuditGroupSet());
    setLogStreams(resetFacets.logStreams);
    setSystemMethods(resetFacets.systemMethods);
    setSystemStatusCodes(resetFacets.systemStatusCodes);
    setLogs([]);
    resetPagination();
    goToPage(1, {
      overrideFilters: emptyFilters,
      forceRefresh: true,
      facetOverrides: resetFacets,
    });
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setLogs([]);
    resetPagination();
    setFetched(false);
    setError('');
  };

  const handlePageSizeChange = (nextSize) => {
    if (nextSize === pageSize) return;
    setPageSize(nextSize);
    resetPagination();
  };

  useEffect(() => {
    resetPagination();
    goToPage(1, { forceRefresh: true });
    // タブ切替時のみ。goToPage の依存でフィルタ変更のたび再取得しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // pageSize 変更後に先頭ページを再取得
  const pageSizeBootRef = useRef(pageSize);
  useEffect(() => {
    if (pageSizeBootRef.current === pageSize) return;
    pageSizeBootRef.current = pageSize;
    goToPage(1, { forceRefresh: true });
  }, [pageSize, goToPage]);

  const toggleAuditGroupCollapse = (groupId) => {
    setCollapsedAuditGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const maxKnownPage = Math.max(1, ...Object.keys(pageCache).map(Number), currentPage);
  const lastKnownHasMore = Boolean(pageCache[maxKnownPage]?.hasMore ?? hasMore);

  const clinicOptions = clinics?.length ? clinics : clinicDirectory;
  const clinicSearchLower = filters.clinicNameSearch.trim().toLowerCase();
  const filteredClinicOptions = clinicSearchLower
    ? clinicOptions.filter((c) =>
      String(c.name || '').toLowerCase().includes(clinicSearchLower)
      || String(c.id || '').toLowerCase().includes(clinicSearchLower)
    )
    : clinicOptions;
  const clinicMap = {};
  clinicOptions.forEach(c => { clinicMap[c.id] = c.name; });

  return (
    <div className="admin-log-viewer">
      <div className="section-header">
        <h1>ログ</h1>
        <p>監査ログとシステムログの閲覧・検索</p>
      </div>

      <div className="admin-log-tabs">
        <button
          className={`admin-log-tab ${tab === 'audit' ? 'active' : ''}`}
          onClick={() => handleTabChange('audit')}
        >
          監査ログ
        </button>
        <button
          className={`admin-log-tab ${tab === 'system' ? 'active' : ''}`}
          onClick={() => handleTabChange('system')}
        >
          システムログ
        </button>
      </div>

      {/* Filters */}
      <section className="admin-log-filter-panel" aria-label="ログ絞り込み">
        <div className="admin-log-filter-panel__header">
          <div>
            <h2 className="admin-log-filter-panel__title">絞り込み</h2>
            <p className="admin-log-filter-panel__subtitle">
              初期は直近7日・AdminHub のみ（高速表示）。必要に応じてソースや期間を広げてください
            </p>
          </div>
          <div className="admin-log-sort-badge" title="サーバー側は日時の新しい順で返します">
            <span className="admin-log-sort-badge__label">表示順</span>
            <span className="admin-log-sort-badge__value">新しい順</span>
          </div>
        </div>

        <div className="admin-log-filter-grid">
          <div className="admin-log-filter-group admin-log-filter-group--wide">
            <label htmlFor="log-clinic-name-search">医院名検索</label>
            <input
              id="log-clinic-name-search"
              type="text"
              value={filters.clinicNameSearch}
              onChange={(e) => setFilters((f) => ({
                ...f,
                clinicNameSearch: e.target.value,
                clinicId: '',
                uid: '',
              }))}
              placeholder="医院名またはID"
            />
          </div>
          <div className="admin-log-filter-group">
            <label htmlFor="log-clinic-select">クリニック</label>
            <select
              id="log-clinic-select"
              className="admin-select"
              value={filters.clinicId}
              onChange={(e) => setFilters((f) => ({
                ...f,
                clinicId: e.target.value,
                uid: '',
                clinicNameSearch: e.target.value
                  ? (filteredClinicOptions.find((c) => c.id === e.target.value)?.name || f.clinicNameSearch)
                  : f.clinicNameSearch,
              }))}
            >
              <option value="">すべて</option>
              {filteredClinicOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.id}</option>
              ))}
            </select>
          </div>
          <div className="admin-log-filter-group">
            <label htmlFor="log-user-select">ユーザー</label>
            <select
              id="log-user-select"
              className="admin-select"
              value={filters.uid}
              onChange={(e) => setFilters((f) => ({ ...f, uid: e.target.value }))}
              disabled={!filters.clinicId || clinicUsersLoading}
            >
              <option value="">
                {!filters.clinicId
                  ? 'クリニックを選択'
                  : clinicUsersLoading
                    ? '読込中...'
                    : 'すべて'}
              </option>
              {clinicUsers.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.displayName || u.userId || u.uid}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-log-filter-group">
            <label htmlFor="log-start-date">開始日</label>
            <input
              id="log-start-date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            />
          </div>
          <div className="admin-log-filter-group">
            <label htmlFor="log-end-date">終了日</label>
            <input
              id="log-end-date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            />
          </div>

          <div className="admin-log-filter-group">
            <label htmlFor="log-deploy-env">環境</label>
            <select
              id="log-deploy-env"
              className="admin-select"
              value={filters.deployEnv}
              onChange={(e) => setFilters((f) => ({ ...f, deployEnv: e.target.value }))}
            >
              <option value="prod">本番 (logs)</option>
              <option value="dev">Staging / dev (logs-dev)</option>
            </select>
          </div>
        </div>

        <div className="admin-log-chip-section">
          <div className="admin-log-chip-section__label">ログソース</div>
          <div className="admin-log-chip-row" role="group" aria-label="ログソース">
            {LOG_STREAM_OPTIONS.map(({ id, label }) => {
              const active = logStreams.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`admin-log-chip${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => {
                    setLogStreams((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) {
                        if (next.size <= 1) return prev;
                        next.delete(id);
                      } else {
                        next.add(id);
                      }
                      return next;
                    });
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === 'system' && (
          <>
            <div className="admin-log-chip-section">
              <div className="admin-log-chip-section__label">HTTP メソッド</div>
              <div className="admin-log-chip-row" role="group" aria-label="HTTP メソッド">
                {SYSTEM_METHOD_OPTIONS.map((method) => {
                  const active = systemMethods.has(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      className={`admin-log-chip admin-log-chip--mono${active ? ' is-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => toggleSetValue(setSystemMethods, SYSTEM_METHOD_OPTIONS, method)}
                    >
                      {method}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="admin-log-chip-section">
              <div className="admin-log-chip-section__label">HTTP ステータス</div>
              <div className="admin-log-chip-row" role="group" aria-label="HTTP ステータス">
                {SYSTEM_STATUS_OPTIONS.map((code) => {
                  const active = systemStatusCodes.has(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`admin-log-chip admin-log-chip--mono${active ? ' is-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => toggleSetValue(setSystemStatusCodes, SYSTEM_STATUS_OPTIONS, code)}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {tab === 'audit' && (
          <details className="admin-log-advanced">
            <summary className="admin-log-advanced__summary">
              <span>監査アクション種別</span>
              <span className="admin-log-advanced__meta">
                {isAuditSetAllEnabled(auditActions)
                  ? 'すべて'
                  : `${auditActions.size}/${ALL_AUDIT_ACTIONS.length} 選択中`}
              </span>
            </summary>
            <div className="admin-log-advanced__body">
              <div className="admin-log-advanced__toolbar">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setAuditActions(createFullSet(ALL_AUDIT_ACTIONS))}
                >
                  すべて選択
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCollapsedAuditGroups(new Set())}
                >
                  すべて展開
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCollapsedAuditGroups(createAllCollapsedAuditGroupSet())}
                >
                  すべて格納
                </button>
              </div>
              <div className="admin-log-action-groups admin-log-action-groups--stack">
                {AUDIT_ACTION_GROUPS.map((group) => (
                  <AuditActionGroup
                    key={group.id}
                    group={group}
                    auditActions={auditActions}
                    setAuditActions={setAuditActions}
                    collapsed={collapsedAuditGroups.has(group.id)}
                    onToggleCollapse={() => toggleAuditGroupCollapse(group.id)}
                  />
                ))}
              </div>
            </div>
          </details>
        )}

        <div className="admin-log-filter-footer">
          <button type="button" className="btn btn-primary btn-search" onClick={handleSearch}>
            検索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            リセット
          </button>
          {tab === 'audit' && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                const params = buildLogQueryParams(filters);
                window.open(buildApiUrl(`/api/admin/logs/audit/export?${params.toString()}`), '_blank');
              }}
            >
              CSV エクスポート
            </button>
          )}
        </div>
      </section>

      {(filters.clinicId || filters.clinicNameSearch || filters.uid
        || filters.startDate !== getDefaultLogStartDate() || filters.endDate
        || filters.deployEnv !== 'prod'
        || !isAuditSetAllEnabled(auditActions)
        || ![...logStreams].every((s) => DEFAULT_LOG_STREAMS.includes(s))
        || logStreams.size !== DEFAULT_LOG_STREAMS.length
        || (tab === 'system' && (
          systemMethods.size < SYSTEM_METHOD_OPTIONS.length
          || systemStatusCodes.size < SYSTEM_STATUS_OPTIONS.length
        ))) && (
        <div className="admin-log-result-bar">
          <span className="admin-log-result-bar__hint">フィルタ適用中</span>
          <span className="admin-log-result-bar__meta">
            環境: {filters.deployEnv === 'dev' ? 'Staging / dev' : '本番'}
            {filters.startDate || filters.endDate
              ? ` / 期間: ${filters.startDate || '—'} 〜 ${filters.endDate || '—'}`
              : ''}
            {` / ソース: ${[...logStreams].map((s) => STREAM_LABELS[s] || s).join(', ')}`}
          </span>
        </div>
      )}

      {/* Content */}
      {loading && (
        <AdminTableShimmer rows={8} columns={5} />
      )}

      {error && (
        <div style={{ color: 'var(--gcp-red)', padding: '1rem 0' }}>{error}</div>
      )}

      {!loading && !error && !fetched && (
        <div className="admin-log-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>直近ログを読み込んでいます。条件を指定して検索できます。</p>
        </div>
      )}

      {!loading && !error && fetched && logs.length === 0 && (
        <div className="admin-log-empty">
          <p>該当するログがありません</p>
        </div>
      )}

      {!loading && !error && logs.length > 0 && tab === 'audit' && (
        <div className="admin-log-table-wrapper">
          <table className="admin-log-table">
            <thead>
              <tr>
                <th>日時</th>
                <th>ソース</th>
                <th>ユーザー</th>
                <th>重要度</th>
                <th>クリニック</th>
                <th>アクション</th>
                <th>概要</th>
                <th>理由</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={`${log.stream}-${log.id}`} onClick={() => setSelectedLog(log)}>
                  <td className="log-ts">{formatTimestamp(log.timestamp)}</td>
                  <td><span className="log-stream-badge">{STREAM_LABELS[log.stream] || log.stream}</span></td>
                  <td>{log.userName || log.uid || '—'}</td>
                  <td>{log.severity || '—'}</td>
                  <td className="log-clinic-cell">{resolveClinicDisplay(log, clinicMap)}</td>
                  <td>
                    {(() => {
                      const { label, isSystem } = formatAuditActionLabel(log);
                      return (
                        <span className={`log-action-badge${isSystem ? ' log-action-badge--system' : ''}`}>
                          {isSystem && <span className="log-system-tag">システム</span>}
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="log-summary" title={log.summary}>{log.summary || '—'}</td>
                  <td className="log-reason" title={log.reason}>{log.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && logs.length > 0 && tab === 'system' && (
        <div className="admin-log-table-wrapper">
          <table className="admin-log-table">
            <thead>
              <tr>
                <th>日時</th>
                <th>ソース</th>
                <th>メソッド</th>
                <th>URL</th>
                <th>ステータス</th>
                <th>時間</th>
                <th>ユーザー</th>
                <th>クリニック</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={`${log.stream}-${log.id}`} onClick={() => setSelectedLog(log)}>
                  <td className="log-ts">{formatTimestamp(log.timestamp)}</td>
                  <td><span className="log-stream-badge">{STREAM_LABELS[log.stream] || log.stream}</span></td>
                  <td>
                    <span className={`log-method-badge ${(log.method || '').toLowerCase()}`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="log-url" title={log.url}>{log.url}</td>
                  <td>
                    <span className={statusClass(log.statusCode)}>
                      {log.statusCode}
                    </span>
                  </td>
                  <td>{log.responseTimeMs != null ? `${log.responseTimeMs}ms` : '—'}</td>
                  <td>{log.uid || '—'}</td>
                  <td className="log-clinic-cell">{resolveClinicDisplay(log, clinicMap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && fetched && (logs.length > 0 || maxKnownPage > 1) && (
        <ListPagination
          mode="cursor"
          currentPage={currentPage}
          pageSize={pageSize}
          maxKnownPage={maxKnownPage}
          hasMore={lastKnownHasMore}
          itemCount={logs.length}
          allowJump
          disabled={loading}
          onPageChange={(page) => goToPage(page)}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="admin-log-detail-overlay" onClick={() => setSelectedLog(null)}>
          <div className="admin-log-detail-modal" onClick={e => e.stopPropagation()}>
            <h3>{tab === 'audit' ? '監査ログ詳細' : 'システムログ詳細'}</h3>

            <div className="admin-log-detail-grid">
              <span className="admin-log-detail-label">ID</span>
              <span className="admin-log-detail-value">{selectedLog.id}</span>

              <span className="admin-log-detail-label">日時</span>
              <span className="admin-log-detail-value">{formatTimestamp(selectedLog.timestamp)}</span>

              <span className="admin-log-detail-label">ソース</span>
              <span className="admin-log-detail-value">{STREAM_LABELS[selectedLog.stream] || selectedLog.stream || '—'}</span>

              {tab === 'audit' && (
                <>
                  <span className="admin-log-detail-label">ユーザー</span>
                  <span className="admin-log-detail-value">
                    {selectedLog.userName} ({selectedLog.uid})
                  </span>

                  <span className="admin-log-detail-label">ロール</span>
                  <span className="admin-log-detail-value">{selectedLog.role || '—'}</span>

                  <span className="admin-log-detail-label">クリニック</span>
                  <span className="admin-log-detail-value">
                    {resolveClinicDisplay(selectedLog, clinicMap)}
                  </span>

                  <span className="admin-log-detail-label">アクション</span>
                  <span className="admin-log-detail-value">
                    {(() => {
                      const { label, isSystem } = formatAuditActionLabel(selectedLog);
                      return (
                        <>
                          {isSystem && <span className="log-system-tag log-system-tag--inline">システム</span>}
                          {label}
                        </>
                      );
                    })()}
                  </span>

                  <span className="admin-log-detail-label">対象</span>
                  <span className="admin-log-detail-value">{selectedLog.target || '—'}</span>

                  <span className="admin-log-detail-label">概要</span>
                  <span className="admin-log-detail-value">{selectedLog.summary || '—'}</span>

                  <span className="admin-log-detail-label">IP</span>
                  <span className="admin-log-detail-value">{selectedLog.ip || '—'}</span>
                </>
              )}

              {tab === 'system' && (
                <>
                  <span className="admin-log-detail-label">メソッド</span>
                  <span className="admin-log-detail-value">{selectedLog.method}</span>

                  <span className="admin-log-detail-label">URL</span>
                  <span className="admin-log-detail-value">{selectedLog.url}</span>

                  <span className="admin-log-detail-label">ステータス</span>
                  <span className="admin-log-detail-value">{selectedLog.statusCode}</span>

                  <span className="admin-log-detail-label">応答時間</span>
                  <span className="admin-log-detail-value">
                    {selectedLog.responseTimeMs != null ? `${selectedLog.responseTimeMs}ms` : '—'}
                  </span>

                  <span className="admin-log-detail-label">UID</span>
                  <span className="admin-log-detail-value">{selectedLog.uid || '—'}</span>

                  <span className="admin-log-detail-label">クリニック</span>
                  <span className="admin-log-detail-value">
                    {resolveClinicDisplay(selectedLog, clinicMap)}
                  </span>

                  <span className="admin-log-detail-label">IP</span>
                  <span className="admin-log-detail-value">{selectedLog.ip || '—'}</span>

                  <span className="admin-log-detail-label">User-Agent</span>
                  <span className="admin-log-detail-value">{selectedLog.userAgent || '—'}</span>

                  {selectedLog.errorMessage && (
                    <>
                      <span className="admin-log-detail-label">エラー</span>
                      <span className="admin-log-detail-value" style={{ color: 'var(--gcp-red)' }}>
                        {selectedLog.errorMessage}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>

            {/* JSON detail */}
            {tab === 'audit' && selectedLog.changes && (
              <>
                <div style={{ marginTop: '1rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                  変更内容
                </div>
                <div className="admin-log-detail-json">
                  {JSON.stringify(selectedLog.changes, null, 2)}
                </div>
              </>
            )}

            {tab === 'system' && selectedLog.requestBody && (
              <>
                <div style={{ marginTop: '1rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                  リクエストボディ
                </div>
                <div className="admin-log-detail-json">
                  {JSON.stringify(selectedLog.requestBody, null, 2)}
                </div>
              </>
            )}

            <button className="admin-log-detail-close" onClick={() => setSelectedLog(null)}>
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogViewer;
